import { UserCredential } from "./../models/domains/pbm/user/user-credential";
import { Injectable } from "@angular/core";

import { AppContext } from "../contexts/app-context";
import { UserCredentials } from "../models/domains/credentials/user-credentials.model";
import { AuthRepository } from "../repositories/auth.repository";
import { UserTermRepository } from "../repositories/user-term.repository";
import { UserTermObserverService } from "./observers/user-term-observer.service";
import { ResetService } from "./observers/reset-observer.service";

@Injectable()
export class AuthService {
  constructor(
    private authRepository: AuthRepository,
    private userTermRepository: UserTermRepository, 
    private userTermObserver: UserTermObserverService,
    private appContext: AppContext, 
    private resetService: ResetService
  ) 
  {}

  public async authenticate(userCredential: UserCredential)  {
    try {
      const userCredentialResult = await this.authRepository.login(userCredential);
      this.appContext.setLoginInfo(userCredentialResult.loginInfo);

      if (userCredentialResult.userTenants.length > 0){
        this.appContext.setCurrentTenant(userCredentialResult.userTenants[0]);
      }
      const term = await this.userTermRepository.validateUserTerm();
      if (term) {
        userCredentialResult.user.setRequiredTerm(term.RequireTerm);
        this.userTermObserver.changeUserTerm(term);
      }
      this.appContext.setUser(userCredentialResult.user);
    } catch (err) {
      console.error(err);
      return await Promise.reject(err);
    }
  }

  logout(): void {
    this.appContext.clearUser();
    this.appContext.clearLoginInfo();
    this.appContext.clearActivePageView();
    this.appContext.clearActiveFeatureView();
    this.appContext.clearCurrentTenant();
    this.resetService.triggerReset();
  }

  async isAuthenticated(): Promise<boolean> {
    const loginInfo = this.appContext.getLoginInfo();
    if (loginInfo && loginInfo.accessToken) {
      return await this.authRepository.validateUserToken(loginInfo.accessToken);
    }
    return false;
  }

  isRequiredTerm(): boolean {
    const user = this.appContext.getUser();
    if (!user.isUnknownUser()) {
      return user.isRequiredTerm();
    }
    return false;
  }

  public isUserLogged(): boolean {
    const loginInfo = this.appContext.getLoginInfo();
    return !!loginInfo && !!loginInfo.accessToken;
  }

  async isServiceTokenvValid(token: string): Promise<boolean> {
    if (token) {
      return await this.authRepository.validateServiceToken(token);
    }
    return false;
  }

  public isServiceLogged(): boolean {
    const serviceCredentials = this.appContext.getServiceCredential();
    return !!serviceCredentials && !!serviceCredentials.token;
  }

  public async switchContext(switchContextHash: string): Promise<UserCredentials> {
    try {
      const hash = JSON.parse(atob(switchContextHash));
      const userCredentials = await this.authRepository.switchContext(hash.UserId, hash.RefreshToken, hash.SourceApplicationId);
      this.appContext.setUserCredential(userCredentials);
      return Promise.resolve(userCredentials);
    } catch (error) {
      return await Promise.reject(error);
    }
  }
}
